import React from 'react';
import {alpha, Box, Chip, Paper, Stack, Typography,} from '@mui/material';
import {Fingerprint, Inventory2, SourceOutlined,} from '@mui/icons-material';

const sourceRows = [{
    source: 'Internal library', detail: 'Curated ingredient record', status: 'Active',
}, {
    source: 'INSA TCA', detail: 'Original food composition source', status: 'Linked',
},];

const identifierRows = [{
    label: 'INSA TCA Code', value: 'Linked record', source: 'INSA TCA',
},];

const supportedIdentifierTypes = ['INSA TCA', 'FDB', 'E-number', 'CHEBI', 'California Proposition 65', 'CAMEO chemicals', 'LiverTox', 'LOTUS', 'NCBI Thesaurus Code', 'UNII', 'External code',];

function StatusPill({children, tone = 'success'}) {
    const isSuccess = tone === 'success';

    return (<Box
            sx={{
                px: 0.65,
                py: 0.28,
                borderRadius: 999,
                fontSize: 9.4,
                fontWeight: 900,
                lineHeight: 1,
                color: isSuccess ? 'success.dark' : 'primary.main',
                bgcolor: (theme) => isSuccess ? alpha(theme.palette.success.main, 0.12) : alpha(theme.palette.primary.main, 0.08),
                border: '1px solid',
                borderColor: (theme) => isSuccess ? alpha(theme.palette.success.main, 0.28) : alpha(theme.palette.primary.main, 0.18),
                whiteSpace: 'nowrap',
            }}
        >
            {children}
        </Box>);
}

function TypePill({children}) {
    return (<Box
            sx={{
                px: 0.65,
                py: 0.28,
                borderRadius: 999,
                fontSize: 9.3,
                fontWeight: 900,
                lineHeight: 1,
                color: 'primary.main',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
                whiteSpace: 'nowrap',
            }}
        >
            {children}
        </Box>);
}

function SourceRow({row, index}) {
    return (<Box
            sx={{
                display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', columnGap: 1, alignItems: 'center', px: 1.05, py: 0.85, borderBottom: index !== sourceRows.length - 1 ? '1px solid' : 'none', borderColor: 'divider',
            }}
        >
            <Box sx={{minWidth: 0}}>
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block', fontWeight: 900, fontSize: 10.6, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}
                >
                    {row.source}
                </Typography>

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        display: 'block', fontWeight: 700, fontSize: 9.6, lineHeight: 1.2, mt: 0.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}
                >
                    {row.detail}
                </Typography>
            </Box>

            <StatusPill>{row.status}</StatusPill>
        </Box>);
}

function IdentifierRow({row, index}) {
    return (<Box
            sx={{
                display: 'grid', gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 0.85fr) auto', columnGap: 0.75, alignItems: 'center', px: 1.05, py: 0.82, borderBottom: index !== identifierRows.length - 1 ? '1px solid' : 'none', borderColor: 'divider',
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 900, fontSize: 10.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
            >
                {row.label}
            </Typography>

            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    fontWeight: 800, fontSize: 10.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
            >
                {row.value}
            </Typography>

            <TypePill>{row.source}</TypePill>
        </Box>);
}

export default function IngredientSourceIdentifiersVisual() {
    return (<Box
            sx={{
                width: '100%', height: '100%', minHeight: 300, p: 1.6, bgcolor: 'background.paper', display: 'flex', alignItems: 'center', overflow: 'hidden',
            }}
        >
            <Paper
                variant="outlined"
                sx={{
                    width: '100%', maxWidth: '100%', borderRadius: 2.5, overflow: 'hidden', bgcolor: 'background.paper', boxShadow: '0 14px 28px rgba(15, 23, 42, 0.05)',
                }}
            >
                {/* Header */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        px: 1.7, py: 1.15, borderBottom: '1px solid', borderColor: 'divider', minWidth: 0,
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{minWidth: 0}}>
                        <Box
                            sx={{
                                width: 28, height: 28, borderRadius: '50%', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1), color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}
                        >
                            <Fingerprint sx={{fontSize: 16}}/>
                        </Box>

                        <Box sx={{minWidth: 0}}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }}
                            >
                                Source data and identifiers
                            </Typography>

                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    display: 'block', mt: 0.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }}
                            >
                                Track original data sources and database-specific ingredient identifiers
                            </Typography>
                        </Box>
                    </Stack>

                    <Chip
                        label="Source-linked"
                        size="small"
                        sx={{
                            height: 23, fontSize: 10.5, fontWeight: 800, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08), color: 'primary.main', flexShrink: 0, ml: 1,
                        }}
                    />
                </Stack>

                {/* Body */}
                <Box
                    sx={{
                        display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '0.95fr 1.25fr'}, minHeight: 245, overflow: 'hidden',
                    }}
                >
                    {/* Left */}
                    <Box
                        sx={{
                            p: 1.55, borderRight: {sm: '1px solid rgba(15, 23, 42, 0.08)'}, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.014), minWidth: 0,
                        }}
                    >
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.15, borderRadius: 2, bgcolor: 'background.paper', mb: 1,
                            }}
                        >
                            <Stack direction="row" spacing={0.8} alignItems="center">
                                <Box
                                    sx={{
                                        width: 30, height: 30, borderRadius: '50%', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.09), color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                    }}
                                >
                                    <Inventory2 sx={{fontSize: 16}}/>
                                </Box>

                                <Box sx={{minWidth: 0}}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block', fontWeight: 900, fontSize: 11.2, lineHeight: 1.15,
                                        }}
                                    >
                                        Pectin
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: 'block', fontWeight: 700, fontSize: 9.8, lineHeight: 1.25, mt: 0.2,
                                        }}
                                    >
                                        Gelling and texture ingredient for fruit and dairy products
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>

                        <Paper
                            variant="outlined"
                            sx={{
                                borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper',
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={0.65}
                                alignItems="center"
                                sx={{
                                    px: 1.05, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', bgcolor: (theme) => alpha(theme.palette.grey[500], 0.035),
                                }}
                            >
                                <SourceOutlined sx={{fontSize: 15, color: 'primary.main'}}/>

                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.35,
                                    }}
                                >
                                    Source data
                                </Typography>
                            </Stack>

                            {sourceRows.map((row, index) => (<SourceRow key={row.source} row={row} index={index}/>))}
                        </Paper>
                    </Box>

                    {/* Right */}
                    <Box
                        sx={{
                            p: 1.55, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1,
                        }}
                    >
                        <Paper
                            variant="outlined"
                            sx={{
                                borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper',
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                    px: 1.05, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', bgcolor: (theme) => alpha(theme.palette.grey[500], 0.035),
                                }}
                            >
                                <Stack direction="row" spacing={0.65} alignItems="center">
                                    <Fingerprint sx={{fontSize: 15, color: 'primary.main'}}/>

                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.35,
                                        }}
                                    >
                                        Active identifier
                                    </Typography>
                                </Stack>

                                <StatusPill>1 linked</StatusPill>
                            </Stack>

                            <Box
                                sx={{
                                    display: 'grid', gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 0.85fr) auto', columnGap: 0.75, px: 1.05, py: 0.65, bgcolor: (theme) => alpha(theme.palette.grey[500], 0.025), borderBottom: '1px solid', borderColor: 'divider',
                                }}
                            >
                                {['Identifier', 'Value', 'Source'].map((head) => (<Typography
                                        key={head}
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{fontWeight: 800, fontSize: 9.7}}
                                    >
                                        {head}
                                    </Typography>))}
                            </Box>

                            {identifierRows.map((row, index) => (<IdentifierRow key={row.label} row={row} index={index}/>))}
                        </Paper>

                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.grey[500], 0.025),
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    display: 'block', mb: 0.7, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.35, fontSize: 9.6,
                                }}
                            >
                                Supported identifier types
                            </Typography>

                            <Stack direction="row" spacing={0.55} flexWrap="wrap" useFlexGap>
                                {supportedIdentifierTypes.map((type) => (<TypePill key={type}>{type}</TypePill>))}
                            </Stack>
                        </Paper>
                    </Box>
                </Box>
            </Paper>
        </Box>);
}